import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { supabase } from '../../../shared/lib/supabase'
import { INDUSTRY_HIERARCHY, hasSubCategories, getIndustry, getDefaultFrameworks } from '../../../shared/data/industries'

const REVENUE_OPTIONS = [
  { value: '', label: 'Select range...' },
  { value: 'Under $25M', label: 'Under $25M' },
  { value: '$25M \u2013 $50M', label: '$25M \u2013 $50M' },
  { value: '$50M \u2013 $100M', label: '$50M \u2013 $100M' },
  { value: '$100M \u2013 $250M', label: '$100M \u2013 $250M' },
  { value: '$250M \u2013 $500M', label: '$250M \u2013 $500M' },
  { value: 'Over $500M', label: 'Over $500M' }
]

const STAFFING_SIZE_OPTIONS = [
  { value: '', label: 'Select size...' },
  { value: '1–15', label: '1–15 employees' },
  { value: '16–50', label: '16–50 employees' },
  { value: '51–250', label: '51–250 employees' },
  { value: '250+', label: '250+ employees' }
]

const IT_TEAM_SIZE_OPTIONS = [
  { value: '', label: 'Select size...' },
  { value: '0', label: 'No dedicated IT' },
  { value: '1-5', label: '1-5 people' },
  { value: '6-20', label: '6-20 people' },
  { value: '21-50', label: '21-50 people' },
  { value: '50+', label: '50+ people' }
]

const GEOGRAPHICAL_PRESENCE_OPTIONS = [
  { value: '', label: 'Select presence...' },
  { value: 'domestic', label: 'Domestic Only (US)' },
  { value: 'international', label: 'Some International Operations' },
  { value: 'global', label: 'Global / Multi-Jurisdiction' }
]

function EditClientModal({ client, onClose }) {
  const navigate = useNavigate()
  const { updateClient, deleteClient } = useAssessment()

  // Form state
  const [name, setName] = useState(client.name || '')
  const [industry, setIndustry] = useState(client.industry || '')
  const [industrySubCategory, setIndustrySubCategory] = useState(client.industrySubCategory || '')
  const [contactName, setContactName] = useState(client.contactName || '')
  const [contactEmail, setContactEmail] = useState(client.contactEmail || '')
  const [secondaryContactName, setSecondaryContactName] = useState(client.secondaryContactName || '')
  const [secondaryContactEmail, setSecondaryContactEmail] = useState(client.secondaryContactEmail || '')
  const [staffingSize, setStaffingSize] = useState(client.staffingSize || '')
  const [annualRevenue, setAnnualRevenue] = useState(client.annualRevenue || '')
  const [itTeamSize, setItTeamSize] = useState(client.itTeamSize || '')
  const [geographicalPresence, setGeographicalPresence] = useState(client.geographicalPresence || '')
  const [domain, setDomain] = useState(client.domain || '')
  const [notes, setNotes] = useState(client.notes || '')
  const [regulatoryFrameworks, setRegulatoryFrameworks] = useState(client.regulatoryFrameworks || [])
  const [logoUrl, setLogoUrl] = useState(client.logoUrl || null)
  const [logoInitials, setLogoInitials] = useState(client.logoInitials || '')
  const [logoColor, setLogoColor] = useState(client.logoColor || '#0D1B2A')
  const [logoUploading, setLogoUploading] = useState(false)
  const logoInputRef = useRef(null)

  const [logoError, setLogoError] = useState('')

  const LOGO_COLORS = [
    { value: '#0D1B2A', label: 'Navy' },
    { value: '#1D4ED8', label: 'Blue' },
    { value: '#0891B2', label: 'Teal' },
    { value: '#059669', label: 'Green' },
    { value: '#7C3AED', label: 'Purple' },
    { value: '#DB2777', label: 'Pink' },
    { value: '#DC2626', label: 'Red' },
    { value: '#D97706', label: 'Amber' },
    { value: '#475569', label: 'Slate' },
    { value: '#1F2937', label: 'Charcoal' },
  ]

  const LOGO_MAX_SIZE = 2 * 1024 * 1024 // 2MB
  const LOGO_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp']

  // Resize image client-side to maxDim x maxDim, output as WebP blob
  const resizeImage = (file, maxDim) => {
    return new Promise((resolve) => {
      // SVGs don't need resizing
      if (file.type === 'image/svg+xml') { resolve(file); return }

      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let w = img.width
        let h = img.height
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = Math.round(h * maxDim / w); w = maxDim }
          else { w = Math.round(w * maxDim / h); h = maxDim }
        }
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, w, h)
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url)
          resolve(blob)
        }, 'image/webp', 0.85)
      }
      img.src = url
    })
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setLogoError('')

    if (!LOGO_ACCEPTED_TYPES.includes(file.type)) {
      setLogoError('File must be PNG, JPG, SVG, or WebP')
      return
    }

    if (file.size > LOGO_MAX_SIZE) {
      setLogoError('File must be under 2MB')
      return
    }

    setLogoUploading(true)
    try {
      // Resize image to max 256x256 before uploading
      const resized = await resizeImage(file, 256)
      const path = `${client.id}/logo.webp`

      const { error: uploadError } = await supabase.storage
        .from('client-logos')
        .upload(path, resized, { upsert: true, contentType: 'image/webp' })

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('client-logos')
        .getPublicUrl(path)

      setLogoUrl(publicUrl)
    } catch (err) {
      console.error('Logo upload failed:', err)
      setLogoError('Upload failed. Check storage permissions.')
    }
    setLogoUploading(false)
  }

  const handleRemoveLogo = async () => {
    try {
      await supabase.storage.from('client-logos').remove([`${client.id}/logo.webp`])
    } catch {
      // Ignore storage errors on delete
    }
    setLogoUrl(null)
  }

  // Reconcile frameworks: swap old auto-defaults for new ones, keep manual additions
  const reconcileFrameworks = (currentFrameworks, oldIndustry, oldSub, newIndustry, newSub) => {
    const oldDefaults = getDefaultFrameworks(oldIndustry, oldSub)
    const newDefaults = getDefaultFrameworks(newIndustry, newSub)
    // Manual = anything in current that wasn't an auto-default
    const manual = currentFrameworks.filter(f => !oldDefaults.includes(f))
    // Merge new defaults + manual, deduplicate
    return [...new Set([...newDefaults, ...manual])]
  }

  // Handle industry change - clear sub-category, reconcile frameworks
  const handleIndustryChange = (newIndustry) => {
    const reconciledFw = reconcileFrameworks(regulatoryFrameworks, industry, industrySubCategory, newIndustry, '')
    setIndustry(newIndustry)
    setIndustrySubCategory('')
    setRegulatoryFrameworks(reconciledFw)
  }

  // Handle sub-category change - reconcile frameworks
  const handleSubCategoryChange = (newSubCategory) => {
    const reconciledFw = reconcileFrameworks(regulatoryFrameworks, industry, industrySubCategory, industry, newSubCategory)
    setIndustrySubCategory(newSubCategory)
    setRegulatoryFrameworks(reconciledFw)
  }

  // Field provenance tracking
  const [fieldProvenance, setFieldProvenance] = useState({})
  const initialValues = useRef({})

  useEffect(() => {
    // Capture initial values for change detection
    initialValues.current = {
      name: client.name || '', industry: client.industry || '',
      industry_sub_category: client.industrySubCategory || '',
      contact_name: client.contactName || '', contact_email: client.contactEmail || '',
      secondary_contact_name: client.secondaryContactName || '',
      secondary_contact_email: client.secondaryContactEmail || '',
      staffing_size: client.staffingSize || '', annual_revenue: client.annualRevenue || '',
      it_team_size: client.itTeamSize || '', geographical_presence: client.geographicalPresence || ''
    }

    // Fetch field provenance from client_intake_submissions
    if (client.id) {
      supabase
        .from('client_intake_submissions')
        .select('field_provenance')
        .eq('client_id', client.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.field_provenance) {
            setFieldProvenance(data.field_provenance)
          }
        })
    }
  }, [client])

  // Map form state names to provenance field keys
  const PROVENANCE_KEY_MAP = {
    name: 'name', industry: 'industry', industrySubCategory: 'industry_sub_category',
    contactName: 'contact_name', contactEmail: 'contact_email',
    secondaryContactName: 'secondary_contact_name', secondaryContactEmail: 'secondary_contact_email',
    staffingSize: 'staffing_size', annualRevenue: 'annual_revenue',
    itTeamSize: 'it_team_size', geographicalPresence: 'geographical_presence'
  }

  // Get provenance indicator for a field
  const getProvenance = (formFieldName) => {
    const key = PROVENANCE_KEY_MAP[formFieldName] || formFieldName
    return fieldProvenance[key] || null
  }

  // UI state
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleSave = async () => {
    if (!name.trim()) return

    setSaving(true)
    setError('')
    try {
      const result = await updateClient(client.id, {
        name: name.trim(),
        industry: industry || null,
        industrySubCategory: industrySubCategory || null,
        contactName: contactName.trim() || null,
        contactEmail: contactEmail.trim() || null,
        secondaryContactName: secondaryContactName.trim() || null,
        secondaryContactEmail: secondaryContactEmail.trim() || null,
        staffingSize: staffingSize || null,
        annualRevenue: annualRevenue || null,
        itTeamSize: itTeamSize || null,
        geographicalPresence: geographicalPresence.trim() || null,
        domain: domain.trim() || null,
        notes: notes.trim() || null,
        regulatoryFrameworks: regulatoryFrameworks,
        logoUrl: logoUrl || null,
        logoInitials: logoInitials.trim() || null,
        logoColor: logoColor || null
      })
      if (result) {
        // Update provenance for fields the admin changed that were client-sourced
        if (Object.keys(fieldProvenance).length > 0) {
          const currentValues = {
            name: name.trim(), industry, industry_sub_category: industrySubCategory,
            contact_name: contactName.trim(), contact_email: contactEmail.trim(),
            secondary_contact_name: secondaryContactName.trim(),
            secondary_contact_email: secondaryContactEmail.trim(),
            staffing_size: staffingSize, annual_revenue: annualRevenue,
            it_team_size: itTeamSize, geographical_presence: geographicalPresence.trim()
          }
          const updatedProvenance = { ...fieldProvenance }
          let changed = false
          for (const [key, prov] of Object.entries(updatedProvenance)) {
            if (prov.source === 'client_intake' && !prov.modified_by_admin) {
              const initial = initialValues.current[key]
              const current = currentValues[key]
              if (initial !== undefined && current !== undefined && initial !== current) {
                updatedProvenance[key] = { ...prov, modified_by_admin: true, modified_at: new Date().toISOString() }
                changed = true
              }
            }
          }
          if (changed) {
            supabase
              .from('client_intake_submissions')
              .update({ field_provenance: updatedProvenance })
              .eq('client_id', client.id)
              .then(() => {})
          }
        }
        onClose()
      } else {
        setError('Failed to save changes. Please try again.')
      }
    } catch (err) {
      console.error('Error saving client:', err)
      setError(err.message || 'An error occurred while saving.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteClient(client.id)
      onClose()
      navigate('/clients')
    } catch (error) {
      console.error('Error deleting client:', error)
    }
  }

  if (showDeleteConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete Client?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This will permanently delete <span className="font-medium">{client.name}</span> and all their assessments. This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-center space-x-3">
            <button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary">
              Cancel
            </button>
            <button onClick={handleDelete} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors">
              Delete Client
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Client</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Form */}
        <div className="space-y-5">
          {/* Client Logo / Identifier */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Client Identifier</h3>
            <div className="flex items-start gap-4">
              {/* Preview */}
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border border-gray-200 dark:border-gray-600">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-lg font-bold text-white" style={{ backgroundColor: logoColor }}>
                    {logoInitials || (client.name || 'C').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-3">
                {/* Image upload */}
                <div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg,.webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoUploading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      {logoUploading ? 'Uploading...' : logoUrl ? 'Change Image' : 'Upload Image'}
                    </button>
                    {logoUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">PNG, JPG, SVG, or WebP. Max 2MB. Auto-resized.</p>
                  {logoError && <p className="text-[11px] text-red-500 mt-0.5">{logoError}</p>}
                </div>

                {/* OR divider */}
                {!logoUrl && (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase">or use initials</span>
                      <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                    </div>

                    {/* Initials input */}
                    <div>
                      <input
                        type="text"
                        value={logoInitials}
                        onChange={(e) => setLogoInitials(e.target.value.toUpperCase().slice(0, 3))}
                        placeholder={`${(client.name || 'C').charAt(0).toUpperCase()}`}
                        maxLength={3}
                        className="w-20 px-2.5 py-1.5 text-sm text-center font-bold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <span className="text-[11px] text-gray-400 dark:text-gray-500 ml-2">1-3 characters</span>
                    </div>

                    {/* Color picker */}
                    <div className="flex items-center gap-1.5">
                      {LOGO_COLORS.map(c => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => setLogoColor(c.value)}
                          title={c.label}
                          className={`w-6 h-6 rounded-md transition-all ${logoColor === c.value ? 'ring-2 ring-offset-1 ring-primary-500 dark:ring-offset-gray-800' : 'hover:scale-110'}`}
                          style={{ backgroundColor: c.value }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Basic Info Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Basic Information</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div style={getProvenance('name') ? { borderLeft: `3px solid ${getProvenance('name').modified_by_admin ? '#D97706' : '#0891B2'}`, paddingLeft: '8px' } : undefined}>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Organization Name <span className="text-red-500">*</span><ProvenanceIndicator provenance={getProvenance('name')} />
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Enter organization name"
                  required
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Primary Domain
                </label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="e.g., acmefinancial.com"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Used for Client Intelligence enrichment</p>
              </div>
              <div style={getProvenance('industry') ? { borderLeft: `3px solid ${getProvenance('industry').modified_by_admin ? '#D97706' : '#0891B2'}`, paddingLeft: '8px' } : undefined}>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Industry<ProvenanceIndicator provenance={getProvenance('industry')} />
                </label>
                <select
                  value={industry}
                  onChange={(e) => handleIndustryChange(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                >
                  <option value="">Select industry...</option>
                  {INDUSTRY_HIERARCHY.map(ind => (
                    <option key={ind.value} value={ind.value}>{ind.label}</option>
                  ))}
                </select>
              </div>
              {/* Sub-Category - only shown when primary industry has sub-categories */}
              {hasSubCategories(industry) && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                  <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Sub-Category
                  </label>
                  <select
                    value={industrySubCategory}
                    onChange={(e) => handleSubCategoryChange(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  >
                    <option value="">Select sub-category...</option>
                    {getIndustry(industry)?.subCategories?.map(sc => (
                      <option key={sc.value} value={sc.value}>{sc.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Business Context Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Business Context</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div style={getProvenance('staffingSize') ? { borderLeft: `3px solid ${getProvenance('staffingSize').modified_by_admin ? '#D97706' : '#0891B2'}`, paddingLeft: '8px' } : undefined}>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Staffing Size<ProvenanceIndicator provenance={getProvenance('staffingSize')} />
                </label>
                <select
                  value={staffingSize}
                  onChange={(e) => setStaffingSize(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                >
                  {STAFFING_SIZE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div style={getProvenance('annualRevenue') ? { borderLeft: `3px solid ${getProvenance('annualRevenue').modified_by_admin ? '#D97706' : '#0891B2'}`, paddingLeft: '8px' } : undefined}>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Annual Revenue<ProvenanceIndicator provenance={getProvenance('annualRevenue')} />
                </label>
                <select
                  value={annualRevenue}
                  onChange={(e) => setAnnualRevenue(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                >
                  {REVENUE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div style={getProvenance('itTeamSize') ? { borderLeft: `3px solid ${getProvenance('itTeamSize').modified_by_admin ? '#D97706' : '#0891B2'}`, paddingLeft: '8px' } : undefined}>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  IT Team Size<ProvenanceIndicator provenance={getProvenance('itTeamSize')} />
                </label>
                <select
                  value={itTeamSize}
                  onChange={(e) => setItTeamSize(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                >
                  {IT_TEAM_SIZE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div style={getProvenance('geographicalPresence') ? { borderLeft: `3px solid ${getProvenance('geographicalPresence').modified_by_admin ? '#D97706' : '#0891B2'}`, paddingLeft: '8px' } : undefined}>
                <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Geographical Presence<ProvenanceIndicator provenance={getProvenance('geographicalPresence')} />
                </label>
                <select
                  value={geographicalPresence}
                  onChange={(e) => setGeographicalPresence(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                >
                  {GEOGRAPHICAL_PRESENCE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Contact Information</h3>

            {/* Primary Contact */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Primary Contact</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div style={getProvenance('contactName') ? { borderLeft: `3px solid ${getProvenance('contactName').modified_by_admin ? '#D97706' : '#0891B2'}`, paddingLeft: '8px' } : undefined}>
                  <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Name<ProvenanceIndicator provenance={getProvenance('contactName')} />
                  </label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Primary contact name"
                  />
                </div>
                <div style={getProvenance('contactEmail') ? { borderLeft: `3px solid ${getProvenance('contactEmail').modified_by_admin ? '#D97706' : '#0891B2'}`, paddingLeft: '8px' } : undefined}>
                  <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Email<ProvenanceIndicator provenance={getProvenance('contactEmail')} />
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="primary@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Secondary Contact */}
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Secondary Contact</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={secondaryContactName}
                    onChange={(e) => setSecondaryContactName(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Secondary contact name"
                  />
                </div>
                <div>
                  <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={secondaryContactEmail}
                    onChange={(e) => setSecondaryContactEmail(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="secondary@example.com"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div>
            <label className="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              placeholder="Additional notes about this client..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Client
          </button>
          <div className="flex space-x-3">
            <button onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !name.trim()} className="btn-primary">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ProvenanceIndicator({ provenance }) {
  if (!provenance) return null
  const isModified = provenance.modified_by_admin
  return (
    <span className="text-[10px] font-medium ml-2" style={{ color: isModified ? '#D97706' : '#0891B2' }}>
      {isModified ? 'Client reported \u00B7 Admin modified' : 'Client reported'}
    </span>
  )
}

export default EditClientModal
